import { Component, OnInit, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { SubscriptionCollector } from '@draeger-academy/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { ResponsiveState } from '@viablelogic/ngx-responsive';

import { GenericModalService } from '../generic-modal/services/generic-modal.service';

import { VideoOptionsInterface, ImageRationsInterface } from './shared/interfaces/video-options.interface';
import * as screenfull from 'screenfull';

const DEFAULT_VIDEO_OPTIONS: VideoOptionsInterface = {
    videoId: null,
    autoplay: false,
    mute: false,
    overlay: false,
    overlayOnMobile: false,
    modestbranding: true,
    showinfo: false,
    rel: false,
    posterUrl: null,
    posterAltText: null,
    aspectRatios: null,
    isQQVideo: false
};

const DefaultAspectRatios: ImageRationsInterface[] = [
    {
        breakpoint: 480,
        width: 480,
        height: 320,
        akamaiImage: null
    },
    {
        breakpoint: 960,
        width: 420,
        height: 280,
        akamaiImage: null
    },
    {
        breakpoint: 1120,
        width: 600,
        height: 400,
        akamaiImage: null
    }
];

@Component({
    selector: 'da-video',
    templateUrl: './video.component.html',
    styleUrls: ['./video.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class VideoComponent implements OnInit {
    @SubscriptionCollector() private _subscriptions;
    @ViewChild('overlay', { static: true }) private _overlay;

    @Input()
    set options(value: VideoOptionsInterface) {
        this._options = { ...DEFAULT_VIDEO_OPTIONS, ...value };
    }
    get options(): VideoOptionsInterface {
        return this._options;
    }
    @Input()
    set data(value: any | string) {
        if (value == null) {
            return;
        }

        if (typeof value === 'string') {
            this._$videoImageUrl.next(value as string);
            this._$videoThumbnailImageUrl.next(value as string);
        } else {
            this._$videoAsset.next(value);
            this._$videoImageUrl.next(value.AssetUrl);
            this._$videoThumbnailImageUrl.next(value.AssetUrl);
        }
    }
    get data(): any | string {
        return this._$videoAsset.getValue();
    }

    public get videoAsset$(): Observable<any> {
        return this._$videoAsset.asObservable();
    }

    public get altText$(): Observable<string> {
        return this._$altText.asObservable();
    }

    public get isMobile$(): Observable<boolean> {
        return this._$isMobile.asObservable();
    }

    public get showVideo$(): Observable<boolean> {
        return this._$showVideo.asObservable();
    }

    public get videoThumbnail$(): Observable<string> {
        return this._$videoThumbnailImageUrl.asObservable();
    }

    public get aspectRatios$(): Observable<ImageRationsInterface[]> {
        return this._$aspectRations.asObservable();
    }

    private _options: VideoOptionsInterface;
    private _$videoAsset: BehaviorSubject<any> = new BehaviorSubject(null);
    private _$isMobile: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$showVideo: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$videoImageUrl: BehaviorSubject<string> = new BehaviorSubject('');
    private _$videoThumbnailImageUrl: BehaviorSubject<string> = new BehaviorSubject('');
    private _$altText: BehaviorSubject<string> = new BehaviorSubject('');
    private _$aspectRations: BehaviorSubject<ImageRationsInterface[]> = new BehaviorSubject(DefaultAspectRatios);

    private _modalId: string;

    constructor(private _responsiveState: ResponsiveState, private _genericModalService: GenericModalService) {}

    ngOnInit() {
        if (this.data != null && typeof this.data === 'object') {
            this._initVideo(this.data);
        }

        if (this._options && this._options.posterUrl) {
            this._$videoThumbnailImageUrl.next(this._options.posterUrl);
        }

        if (this._options && (this._options.altText || this._options.posterAltText)) {
            this._$altText.next(this._options.posterAltText ? this._options.posterAltText : this._options.altText);
        }

        if (this._options && this._options.aspectRatios) {
            this._$aspectRations.next(this._options.aspectRatios);
        }

        this._setVideoThumbnails();
        this._resizeHandler();
        this._overlayHandler();
    }

    private _setVideoThumbnails(): void {
        const aspectRatios = this._$aspectRations.getValue();
        aspectRatios.forEach((i) => {
            i.akamaiImage = this._$videoThumbnailImageUrl.getValue();
        });
        this._$aspectRations.next(JSON.parse(JSON.stringify(aspectRatios)));
    }

    private _resizeHandler(): void {
        this._subscriptions = this._responsiveState.elemento$.subscribe((val) => {
            const _showMobile = val === 'xs' || val === 'sm';
            this._$isMobile.next(_showMobile);
            if (this.options) this.options.autoplay = !_showMobile;
            if (_showMobile) {
                this.options.overlay = false;
            }

            if (this._modalId != null && !screenfull.default.isFullscreen) {
                this._genericModalService.close(this._modalId);
            } else if (this._$showVideo.getValue() && !this._$isMobile.getValue()) {
                this._$showVideo.next(false);
            }
        });
    }

    private _overlayHandler(): void {
        this._subscriptions = this.showVideo$.subscribe((data) => {
            if (data && this.options.overlay && !this._$isMobile.getValue()) {
                this._modalId = this._genericModalService.create(this._overlay);
                this._genericModalService.open(this._modalId, 'da-modal');

                // Subscribe on modal close
                this._subscriptions = this._genericModalService.onClose(this._modalId).subscribe(() => {
                    this._$showVideo.next(false);
                    this._modalId = null;
                });
            }
        });
    }

    playVideo() {
        if (this._$showVideo.getValue()) {
            return;
        }
        if (!!this._options.videoId) {
            if (this._options.isQQVideo) {
                this._options.videoId = this._getQQVideoId(this._options.videoId);
            }
            this._$showVideo.next(true);
        } else {
            if (this._$videoAsset.getValue() != null) {
                this._$showVideo.next(true);
            } else {
                // this._assetService
                //     .resolveFromUrl(this._$videoImageUrl.getValue())
                //     .then((data) => {
                //         this._initVideo(data);
                //         this._$videoAsset.next(data);
                //         this._$showVideo.next(true);
                //     })
                //     .catch((error) => {
                //         // TODO: define error
                //     });
            }
        }
    }

    private _initVideo(data: any) {
        if (!!data.AdditionalAssetUrl) {
            if (data.AdditionalAssetUrl.toLowerCase().indexOf('.qq.com') > 0) {
                this._options.isQQVideo = true;
                this._options.videoId = this._getQQVideoId(data.AdditionalAssetUrl);
            } else {
                this._options.videoId = this._getYoutubeVideoId(data.AdditionalAssetUrl);
            }
        } else if (!!data.AssetUrl) {
            // only for prototype. It should be implemented with servide call of asset manager
            const v = data.AssetUrl.match(/youtube_(.*?).jpg/);
            if (!!v) {
                this._options.videoId = v[1];
            }
        }
    }

    private _getYoutubeVideoId(u: string): string {
        let id = '';
        const url = u.replace(/(>|<)/gi, '').split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
        if (url[2] !== undefined) {
            id = url[2].split(/[^0-9a-z_\-]/i)[0];
        } else {
            id = u;
        }
        return id;
    }

    private _getQQVideoId(url: string): string {
        let id = '';
        if (url.indexOf('.html?vid=') > 0) {
            id = url.split('vid=')[1];
        } else if (url.indexOf('.html') > 0) {
            id = url.substring(url.lastIndexOf('/') + 1, url.lastIndexOf('.html'));
        } else if (url.indexOf('http') < 0) {
            id = url;
        }
        return id;
    }
}
